import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Snackbar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import BrandSpinner from "../../../shared/components/BrandSpinner";
import AddIcon from "@mui/icons-material/Add";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";

import api from "../../../services/api/axios";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import useIncubatorsData from "./hooks/useIncubatorsData";
import useRowSelection from "../../../hooks/useRowSelection";
import SearchBar from "../../../shared/components/SearchBar";
import BrandPagination from "../../../layouts/Pagination";
import {
  ACTION_MENU_ICON_SX,
  ACTION_MENU_ICON_TONES,
  ACTION_MENU_PAPER_SX,
  ACTIONS_HEADER_BTN_SX,
} from "../../../theme/buttonstyles";
import EntityProfile from "../../../shared/components/EntityProfile";
import DeleteConfirmDialog from "../../../shared/components/DeleteConfirmDialog";
import { incubatorProfileConfig } from "../../../shared/profile-configs/IncubatorProfileConfig";
import IncubatorsTable from "./IncubatorsTable";
import { downloadBlobFile } from "../companies/BRECompanyHelpers";
import { INCUBATOR_PIPELINE_OPTIONS } from "./pipelineOptions";

const BRAND_BLUE = "#00336C";
const PAGE_BG = "#f7f9fc";

const CARD_SX = {
  border: `1.5px solid ${BRAND_BLUE}`,
  borderRadius: 2,
  boxShadow: "0 8px 20px rgba(15, 23, 42, 0.08)",
  background: "#ffffff",
  overflow: "hidden",
};

const PAGE_SIZE = 50;

function Incubators() {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
  const [pipelineStatusFilter, setPipelineStatusFilter] = useState("");
  const [orderBy, setOrderBy] = useState("company_name");
  const [orderDirection, setOrderDirection] = useState("asc");

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [profileOpen, setProfileOpen] = useState(false);
  const [selectedIncubator, setSelectedIncubator] = useState(null);
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const actionsMenuOpen = Boolean(actionsAnchorEl);

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbarMsg(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  const { incubators, totalCount, loading, error, refetch } = useIncubatorsData(
    {
      page,
      pageSize: PAGE_SIZE,
      searchTerm: debouncedSearchTerm,
      pipelineStatus: pipelineStatusFilter,
      orderBy,
      orderDirection,
    },
  );

  const getIncubatorId = useCallback((incubator) => incubator.id, []);
  const {
    selectedIds,
    selectedCount,
    allSelected,
    someSelected,
    toggleOne,
    togglePage,
    setSelectedIds,
    clear: clearSelection,
  } = useRowSelection(incubators, getIncubatorId);

  const columns = useMemo(
    () => [
      { id: "company_name", label: "Company", sortable: true },
      { id: "address", label: "TechPlace Address", sortable: true },
      { id: "contact_name", label: "Name", sortable: true },
      { id: "contact_telephone", label: "Phone", sortable: true },
      { id: "contact_email", label: "Email", sortable: true },
      { id: "explore", label: "Explore", sortable: false },
    ],
    [],
  );

  const incubatorProfileSections = useMemo(
    () => incubatorProfileConfig(selectedIncubator || {}),
    [selectedIncubator],
  );

  const handleSortRequest = useCallback(
    (key, explicitDirection = null) => {
      if (explicitDirection) {
        setOrderBy(key);
        setOrderDirection(explicitDirection);
      } else {
        const isAsc = orderBy === key && orderDirection === "asc";
        setOrderBy(key);
        setOrderDirection(isAsc ? "desc" : "asc");
      }
      setPage(1);
    },
    [orderBy, orderDirection],
  );

  const handleAdd = useCallback(() => {
    navigate("/incubators/new");
  }, [navigate]);

  const handleEdit = useCallback(
    (incubator) => {
      navigate(`/incubators/${incubator.id}`);
    },
    [navigate],
  );

  const handleOpenProfile = useCallback((incubator) => {
    setSelectedIncubator(incubator);
    setProfileOpen(true);
  }, []);

  const handleUpdatePipelineStatus = useCallback(
    async (incubatorId, pipelineStatus) => {
      try {
        await api.patch(`/incubators/${incubatorId}`, {
          pipeline_status: pipelineStatus === "" ? null : pipelineStatus,
        });

        await refetch();
        setSelectedIncubator((prev) =>
          prev?.id === incubatorId
            ? { ...prev, pipeline_status: pipelineStatus }
            : prev,
        );
      } catch {
        showSnackbar("Failed to update incubator status.", "error");
      }
    },
    [refetch, showSnackbar],
  );

  const handleCloseProfile = useCallback(() => {
    setProfileOpen(false);
    setSelectedIncubator(null);
  }, []);

  const handleExport = useCallback(() => {
    api
      .get("/incubators/export", {
        responseType: "blob",
        params: {
          search: debouncedSearchTerm || undefined,
          pipeline_status: pipelineStatusFilter || undefined,
        },
      })
      .then((response) => {
        downloadBlobFile(response.data, "Incubators.xlsx");
      })
      .catch(() => {
        showSnackbar("Export failed. Please try again.", "error");
      });
  }, [debouncedSearchTerm, pipelineStatusFilter, showSnackbar]);

  const doDeleteIncubators = useCallback(
    async (ids) => {
      setDeleting(true);
      const results = await Promise.allSettled(
        ids.map((id) => api.delete(`/incubators/${id}`)),
      );
      const succeeded = results.filter(
        (result) => result.status === "fulfilled",
      ).length;
      const failed = results.length - succeeded;

      if (succeeded > 0) {
        showSnackbar(
          `Deleted ${succeeded} incubator${succeeded === 1 ? "" : "s"} successfully.`,
        );
      }
      if (failed > 0) {
        showSnackbar(`Deleted ${succeeded}, but ${failed} failed.`, "warning");
      }

      setDeleting(false);
      setDeleteConfirmOpen(false);
      await refetch();
      setSelectedIds(new Set());
    },
    [refetch, setSelectedIds, showSnackbar],
  );

  const handleDeleteSelected = useCallback(() => {
    if (selectedIds.size === 0) {
      showSnackbar("No rows selected.", "info");
      return;
    }
    setDeleteConfirmOpen(true);
  }, [selectedIds, showSnackbar]);

  const confirmDeleteSelected = useCallback(async () => {
    await doDeleteIncubators(Array.from(selectedIds));
  }, [doDeleteIncubators, selectedIds]);

  const handleOpenActionsMenu = useCallback((event) => {
    setActionsAnchorEl(event.currentTarget);
  }, []);

  const handleCloseActionsMenu = useCallback(() => {
    setActionsAnchorEl(null);
  }, []);

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        backgroundColor: PAGE_BG,
        pb: 4,
      }}
    >
      {/* ===== Sticky Header ===== */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          borderBottom: "1px solid #dce4ef",
          background: "#ffffff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: { xs: 2, md: 3 },
            py: 1.75,
            flexWrap: "wrap",
          }}
        >
          {/* Left: icon + title */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                background: "#648315",
                color: "#ffffff",
                display: "grid",
                placeItems: "center",
                flex: "0 0 auto",
              }}
            >
              <RocketLaunchIcon sx={{ fontSize: 19 }} />
            </Box>
            <Box
              sx={{
                fontSize: 21,
                fontWeight: 800,
                color: BRAND_BLUE,
                lineHeight: 1.05,
                whiteSpace: "nowrap",
              }}
            >
              Incubators
            </Box>
          </Box>

          {/* Center: search */}
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <SearchBar
              value={searchTerm}
              onChange={(value) => {
                setSearchTerm(value);
                setPage(1);
              }}
              placeholder="Search Incubators by Company or Contact..."
              maxWidth={1700}
            />
          </Box>

          {/* Right: actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
              flexWrap: "wrap",
            }}
          >
            {selectedCount > 0 && (
              <Chip
                label={`${selectedCount} selected`}
                size="small"
                sx={{
                  height: 26,
                  fontSize: 12,
                  fontWeight: 700,
                  bgcolor: "rgba(0,51,108,0.08)",
                  color: BRAND_BLUE,
                }}
              />
            )}
            <Button
              endIcon={<KeyboardArrowDownIcon />}
              onClick={handleOpenActionsMenu}
              sx={ACTIONS_HEADER_BTN_SX}
            >
              Actions
            </Button>

            <Menu
              anchorEl={actionsAnchorEl}
              open={actionsMenuOpen}
              onClose={handleCloseActionsMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{ sx: ACTION_MENU_PAPER_SX }}
            >
              <MenuItem
                onClick={() => {
                  handleCloseActionsMenu();
                  handleAdd();
                }}
              >
                <ListItemIcon
                  sx={{ ...ACTION_MENU_ICON_SX, ...ACTION_MENU_ICON_TONES.add }}
                >
                  <AddIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Add Incubator</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleCloseActionsMenu();
                  handleExport();
                }}
                disabled={incubators.length === 0}
              >
                <ListItemIcon
                  sx={{
                    ...ACTION_MENU_ICON_SX,
                    ...ACTION_MENU_ICON_TONES.export,
                  }}
                >
                  <FileDownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleCloseActionsMenu();
                  clearSelection();
                }}
                disabled={selectedCount === 0}
              >
                <ListItemIcon
                  sx={{
                    ...ACTION_MENU_ICON_SX,
                    ...ACTION_MENU_ICON_TONES.clear,
                  }}
                >
                  <ClearAllIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Clear Selection</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={async () => {
                  handleCloseActionsMenu();
                  await handleDeleteSelected();
                }}
                disabled={selectedCount === 0}
              >
                <ListItemIcon
                  sx={{
                    ...ACTION_MENU_ICON_SX,
                    ...ACTION_MENU_ICON_TONES.delete,
                  }}
                >
                  <DeleteForeverIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{`Delete Selected (${selectedCount})`}</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Box>

      {/* ===== Content Area ===== */}
      <Box sx={{ px: { xs: 2, md: 3 }, mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && incubators.length === 0 ? (
          <BrandSpinner compact py={8} />
        ) : (
          <>
            <Card sx={CARD_SX}>
              <IncubatorsTable
                incubators={incubators}
                columns={columns}
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={handleSortRequest}
                pipelineStatusFilter={pipelineStatusFilter}
                onPipelineStatusFilterChange={(value) => {
                  setPipelineStatusFilter(value);
                  setPage(1);
                }}
                pipelineStatusOptions={INCUBATOR_PIPELINE_OPTIONS}
                selectedIds={selectedIds}
                allSelected={allSelected}
                someSelected={someSelected}
                toggleOne={toggleOne}
                togglePage={togglePage}
                onEdit={handleEdit}
                onOpenProfile={handleOpenProfile}
                onUpdatePipelineStatus={handleUpdatePipelineStatus}
              />
            </Card>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <BrandPagination
                page={page}
                count={Math.ceil(totalCount / PAGE_SIZE) || 1}
                onChange={(_event, value) => setPage(value)}
              />
            </Box>
          </>
        )}
      </Box>

      <EntityProfile
        open={profileOpen}
        onClose={handleCloseProfile}
        entity={selectedIncubator}
        sections={incubatorProfileSections}
        dialogPaperSx={{
          borderRadius: "16px",
          overflow: "hidden",
          backgroundColor: "#00336C",
          border: "1px solid #00336C",
        }}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>

      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => !deleting && setDeleteConfirmOpen(false)}
        onConfirm={confirmDeleteSelected}
        loading={deleting}
        title="Delete Incubators"
        description={`Are you sure you want to delete ${selectedIds.size} incubator${selectedIds.size === 1 ? "" : "s"}? This action can't be undone.`}
      />
    </Box>
  );
}

export default Incubators;
